// Adding a picture or a video to a dish (ADR 0097): check the file here so the
// owner hears at once, then the same three steps as a dish photo.
//
//   1. POST .../media/upload-url  a presigned PUT into the private bucket
//   2. PUT the bytes straight to storage (progress)
//   3. POST .../media/confirm     the server checks the file arrived and queues it
//
// The limits mirror the server's (apps/api menu-media.schemas.ts); the server is the
// one that enforces them. Nothing is retried here: after an upload whose confirm got no
// answer, adding the file again is safe because each call is idempotent on its key.

import { apiFetch } from './client.js';
import { putWithProgress } from './photo-upload.js';
import type { ItemMediaView, MediaKind, MediaUploadUrlResponse } from './types.js';

export const MEDIA_LIMITS = {
  maxPerItem: 8,
  maxVideosPerItem: 2,
  maxPhotoBytes: 10 * 1024 * 1024,
  maxVideoBytes: 60 * 1024 * 1024,
} as const;

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

// Some browsers leave the type empty for a phone's .mov; the name is then the only hint.
const BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

export type MediaFileCheck =
  | { readonly ok: true; readonly kind: MediaKind; readonly contentType: string }
  | { readonly ok: false; readonly message: string };

export function checkMediaFile(file: File): MediaFileCheck {
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  const contentType = file.type !== '' ? file.type : (BY_EXTENSION[ext] ?? '');
  if (file.size === 0) return { ok: false, message: 'This file is empty.' };
  if (PHOTO_TYPES.includes(contentType)) {
    if (file.size > MEDIA_LIMITS.maxPhotoBytes) {
      return { ok: false, message: 'Pictures can be up to 10 MB.' };
    }
    return { ok: true, kind: 'photo', contentType };
  }
  if (VIDEO_TYPES.includes(contentType)) {
    if (file.size > MEDIA_LIMITS.maxVideoBytes) {
      return { ok: false, message: 'Videos can be up to 60 MB. Record a shorter clip.' };
    }
    return { ok: true, kind: 'video', contentType };
  }
  return {
    ok: false,
    message: 'Use a JPEG, PNG, WebP or AVIF picture, or an MP4, MOV or WebM video.',
  };
}

export interface MediaUploadArgs {
  readonly basePath: string; // "/organizations/:orgId/menus/:menuId/items/:itemId"
  readonly file: File;
  readonly onProgress?: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

export async function uploadItemMedia(args: MediaUploadArgs): Promise<ItemMediaView> {
  const check = checkMediaFile(args.file);
  if (!check.ok) throw new Error(check.message);
  const signalInit = args.signal ? { signal: args.signal } : {};

  const minted = await apiFetch<MediaUploadUrlResponse>(`${args.basePath}/media/upload-url`, {
    method: 'POST',
    body: { kind: check.kind, contentType: check.contentType, sizeBytes: args.file.size },
    ...signalInit,
  });
  await putWithProgress(
    minted.uploadUrl,
    args.file,
    minted.requiredHeaders,
    args.onProgress,
    args.signal,
  );
  const confirmed = await apiFetch<{ media: ItemMediaView }>(`${args.basePath}/media/confirm`, {
    method: 'POST',
    body: { objectKey: minted.objectKey },
    ...signalInit,
  });
  return confirmed.media;
}
